/**
 * @file passengers.api.js
 * @module PassengersAPI
 * @description HTTP API wrapper for passenger-related endpoints. Methods return Axios promises.
 */

import { axiosInstance } from "./axios";

export const PassengersAPI = {
    /**
     * Create a passenger.
     * @param {Object} passenger - Passenger payload.
     * @param {string} [token=""] - Optional auth token.
     * @returns {Promise} Axios promise resolving to created passenger.
     */
    createPassenger: (passenger, token = "") => {
        return axiosInstance(token).post(`/passengers`, passenger)
    },

    /**
     * Patch attributes of a passenger by id.
     * @param {string} passengerId
     * @param {Object} passenger - Partial payload to update.
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to updated passenger.
     */
    updateAnyAttributeById: (passengerId, passenger, token = "") => {
        return axiosInstance(token).patch(`/passengers/${passengerId}`, passenger)
    },

    /**
     * Delete a passenger by id.
     * @param {string} passengerId
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to deletion result.
     */
    deletePassengerById: (passengerId, token = "") => {
        return axiosInstance(token).delete(`/passengers/${passengerId}`)
    },

    /**
     * Delete many passengers by payload.
     * @param {Array|Object} passengers
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to deletion result.
     */
    deleteManyPassengersById: (passengers, token = "") => {
        return axiosInstance(token).post(`/bulk-delete-passengers`, passengers)
    },

    /**
     * Check if passengers exist (used in bulk imports).
     * @param {Object} payload - Payload describing passengers to check.
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to check result.
     */
    checkPassengers: (payload, token = "") => {
        return axiosInstance(token).post(`/passengers-exists`, payload)
    },

    /**
     * Get passengers for a given customer.
     * @param {string} customerId
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to passengers list.
     */
    getPassengersByCustomerId: (customerId, token = "") => {
        return axiosInstance(token).get(`/customers/${customerId}/passengers`)
    },

    /**
     * Bulk create passengers and associate to a customer.
     * @param {Array} passengers - Array of passenger objects.
     * @param {string} customerId
     * @param {string} customerType
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to bulk create result.
     */
    bulkCreatePassengers: (passengers = [], customerId, customerType, token = "") => {
        return axiosInstance(token).post(`/bulk-create-passengers`, { passengers: passengers, customerId: customerId, customerType: customerType })
    }
}
